import { decodeFunctionData } from 'viem'
import type { Abi, PublicClient } from 'viem'
import { EthSafeTransaction } from '@safe-global/protocol-kit'
import STNGImplementationAbi from '@/abis/STNGImplementation.json'
import TreasuryRecallModuleAbi from '@/abis/TreasuryRecallModule.json'
import OperationalSafeFactoryAbi from '@/abis/OperationalSafeFactory.json'
import SafeGuardAbi from '@/abis/SafeGuard.json'
import {
  SAFE_TRANSACTION_VALUE_DEFAULT,
  SAFE_TRANSACTION_DATA_DEFAULT,
  SAFE_TRANSACTION_VALUE_HEX_ZERO,
  SAFE_TRANSACTION_DATA_HEX_ZERO,
} from '@/constants/main'
import { TransactionType } from './types'

export const formatDate = (dateString: string | null): string => {
  if (!dateString) return 'N/A'
  try {
    const date = new Date(dateString)
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return dateString
  }
}

export const parseLocalDate = (dateString: string): Date | null => {
  if (!dateString) return null
  const [year, month, day] = dateString.split('-').map(Number)
  if (isNaN(year) || isNaN(month) || isNaN(day)) return null
  // Create date in local timezone (month is 0-indexed)
  return new Date(year, month - 1, day)
}

export const formatLocalDate = (date: Date | null): string => {
  if (!date) return ''
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export const extractAddressFromData = (
  data: string | undefined
): string | undefined => {
  if (!data || data === SAFE_TRANSACTION_DATA_DEFAULT) {
    return undefined
  }

  try {
    const dataWithoutPrefix = data.startsWith('0x') ? data.slice(2) : data

    if (dataWithoutPrefix.length < 40) {
      return undefined
    }

    const addressHex = dataWithoutPrefix.slice(-40)

    return `0x${addressHex}`
  } catch (error) {
    console.error('Error extracting address from data:', error)
    return undefined
  }
}

export const formatDateForFilter = (
  dateString: string,
  isEndOfDay: boolean
): string => {
  const date = new Date(dateString)
  if (isEndOfDay) {
    date.setHours(23, 59, 59, 999)
  } else {
    date.setHours(0, 0, 0, 0)
  }
  return date.toISOString()
}

export const getTransactionType = async (
  tx: EthSafeTransaction,
  safeAddress: string,
  stngProxy: string | undefined,
  operationalSafeFactory: string | undefined,
  publicClient: PublicClient | null | undefined
): Promise<string> => {
  const toAddress = tx.data.to?.toLowerCase()
  const safeAddressLower = safeAddress.toLowerCase()
  const value = tx.data.value || SAFE_TRANSACTION_VALUE_DEFAULT
  const data = tx.data.data || SAFE_TRANSACTION_DATA_DEFAULT

  const valueStr = typeof value === 'string' ? value : String(value)

  if (
    toAddress === safeAddressLower &&
    (valueStr === SAFE_TRANSACTION_VALUE_DEFAULT ||
      valueStr === SAFE_TRANSACTION_VALUE_HEX_ZERO) &&
    (data === SAFE_TRANSACTION_DATA_DEFAULT ||
      data === SAFE_TRANSACTION_DATA_HEX_ZERO ||
      !data)
  ) {
    return 'Reject'
  }
  try {
    const txData = tx.data
    const toAddress = txData.to?.toLowerCase()
    const data = txData.data || SAFE_TRANSACTION_DATA_DEFAULT

    if (!data || data === SAFE_TRANSACTION_DATA_DEFAULT) {
      return 'Unknown'
    }

    if (toAddress === stngProxy?.toLowerCase()) {
      try {
        const decoded = decodeFunctionData({
          abi: STNGImplementationAbi as Abi,
          data: data as `0x${string}`,
        })

        if (decoded.functionName === TransactionType.Mint) {
          return 'Mint'
        }
        if (decoded.functionName === TransactionType.Burn) {
          return 'Burn'
        }
        if (decoded.functionName === TransactionType.Transfer) {
          return 'Transfer To'
        }
      } catch (error) {
        console.error('Error decoding function data:', error)
      }
    }

    if (publicClient && operationalSafeFactory) {
      try {
        if (toAddress === operationalSafeFactory.toLowerCase()) {
          try {
            const decoded = decodeFunctionData({
              abi: OperationalSafeFactoryAbi as Abi,
              data: data as `0x${string}`,
            })

            if (decoded.functionName === TransactionType.Delete) {
              return 'Delete'
            }
          } catch (error) {
            console.error(
              'Error decoding OperationalSafeFactory function data:',
              error
            )
          }
        }

        try {
          const operationalSafeGuard = (await publicClient.readContract({
            address: operationalSafeFactory as `0x${string}`,
            abi: OperationalSafeFactoryAbi as Abi,
            functionName: 'operationalSafeGuard',
          })) as string

          if (
            operationalSafeGuard &&
            toAddress === operationalSafeGuard.toLowerCase()
          ) {
            try {
              const decoded = decodeFunctionData({
                abi: SafeGuardAbi as Abi,
                data: data as `0x${string}`,
              })

              if (decoded.functionName === TransactionType.Block) {
                return 'Block'
              }
              if (decoded.functionName === TransactionType.Unblock) {
                return 'Unblock'
              }
            } catch (error) {
              console.error('Error decoding SafeGuard function data:', error)
            }
          }
        } catch (error) {
          console.error('Error fetching operationalSafeGuard:', error)
        }

        try {
          const treasuryRecallModule = (await publicClient.readContract({
            address: operationalSafeFactory as `0x${string}`,
            abi: OperationalSafeFactoryAbi as Abi,
            functionName: 'treasuryRecallModule',
          })) as string

          if (
            treasuryRecallModule &&
            toAddress === treasuryRecallModule.toLowerCase()
          ) {
            try {
              const decoded = decodeFunctionData({
                abi: TreasuryRecallModuleAbi as Abi,
                data: data as `0x${string}`,
              })

              if (decoded.functionName === 'recall') {
                return 'Transfer From'
              }
            } catch (error) {
              console.error('Error decoding function data:', error)
            }
          }
        } catch (error) {
          console.error('Error fetching treasuryRecallModule:', error)
        }
      } catch (error) {
        console.error('Error fetching contract addresses:', error)
      }
    }

    return 'Treasury Update'
  } catch (error) {
    console.error('Error determining transaction type:', error)
    return 'Unknown'
  }
}

export const getContractName = (
  address: string,
  stngProxy: string | undefined,
  treasurySafe: string | undefined,
  operationalSafeFactory: string | undefined,
  operationalSafeGuard?: string | undefined
): string => {
  const toAddress = address.toLowerCase()
  if (toAddress === stngProxy?.toLowerCase()) {
    return 'dKZT Token'
  }
  if (toAddress === treasurySafe?.toLowerCase()) {
    return 'Treasury Safe'
  }
  if (toAddress === operationalSafeFactory?.toLowerCase()) {
    return 'OperationalSafeFactory'
  }
  if (
    operationalSafeGuard &&
    toAddress === operationalSafeGuard.toLowerCase()
  ) {
    return 'OperationalSafeGuard'
  }
  return ''
}
